import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import { useHandleNurseChange } from "@/hooks/useHandleNurseChange";
import { useScheduleSaves } from "@/hooks/useScheduleSaves";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import SystemButton from "@/ui/SystemButton/SystemButton";
import type { NurseInShift } from "@/utils/models";

type PostNoteDialogProps = {
	open: boolean;
	nurseInShift: NurseInShift;
	date: DateTime;
	shiftType: string;
	showActual?: boolean;
	onClose: () => void;
	onSave: () => void;
};

export const PostNoteDialog: FC<PostNoteDialogProps> = ({ open, nurseInShift, date, shiftType, showActual, onClose, onSave }) => {
	const { t } = useTranslation();

	const { updateNurseNotes } = useScheduleSaves();
	const showErrorSnackbar = useErrorSnackbar();
	const { updateNurse, isLoading } = useHandleNurseChange();
	const [notes, setNotes] = useState<string>(nurseInShift.notes || "");
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();

	const handleSave = useCallback(async () => {
		try {
			await updateNurse({ nurse: { ...nurseInShift, notes }, date, shiftType, isActual: showActual });

			updateNurseNotes(nurseInShift.id, date, shiftType, notes, !!showActual);

			track("shift_note_posted", {
				nurse_id: nurseInShift.nurseId,
				shift_type: shiftType,
				date: date.toFormat("MM/DD/yyyy"),
				has_note: !!notes,
			});

			showSnackbar(t("shift.note.saved.success"), "success");
			onSave();
		} catch (error) {
			showErrorSnackbar("Failed to save shift note", { nurseId: nurseInShift.id }, error);
		}
	}, [notes, nurseInShift, date, shiftType, showActual, updateNurse, updateNurseNotes, showSnackbar, showErrorSnackbar, t, onSave, track]);

	const handleClose = useCallback(() => {
		setNotes(nurseInShift.notes || "");
		onClose();
	}, [nurseInShift.notes, onClose]);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="post-note-dialog">
			<DialogTitle>{t("post.shift.note")}</DialogTitle>
			<DialogContent>
				<Box sx={{ mt: 1 }}>
					<SystemValidationTextField
						autoFocus
						fullWidth
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder={t("enter.shift.note.placeholder")}
						dataTestId="post-note-notes-input"
						injectedProps={{
							multiline: true,
							rows: 4,
						}}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<SystemButton onClick={handleClose} buttonVariant="borderless" sx={{ color: "primary.main" }} data-testid="post-note-cancel-button">
					{t("cancel")}
				</SystemButton>
				<SystemButton
					onClick={handleSave}
					buttonVariant="borderless"
					loading={isLoading.updateNurse}
					sx={{ color: "primary.main" }}
					data-testid="post-note-submit-button"
				>
					{t("save")}
				</SystemButton>
			</DialogActions>
		</Dialog>
	);
};
